import React, { type ReactNode, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/router'
import { LikeButton } from 'shared/components/like/like-button'
import useUser from 'shared/hooks/use-user'
import { Comment } from 'modules/comments/components/comment'
import { CommentsContext } from 'modules/comments/components/comments-list'
import type { CommentsContextType } from 'modules/comments/components/comments-list/comments-list.types'
import { CommentHighlightProvider } from 'modules/comments/contexts/comment-highlight'
import { CommentSortOptions, CommentsTypeEnum } from 'modules/comments/types'
import { shouldHideUnderModerationContent } from 'modules/community/components/under-moderation-placeholder'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import type {
  PostCommentInfoResult,
  PostCommentsInfoInterface,
} from 'modules/community/types/post-info'
import type {
  CommentsSearchInterface,
  PostInterface,
  SearchPostInterface,
} from 'modules/community/types/post-interface'
import { isSearchPostGuard } from 'modules/community/types/post-interface'
import { PostPinnedActions } from '../pinned-post/components/post-pinned-actions'
import { PostActionsMenu } from '../post/components/post-actions-menu'
import PostModal from '../post/post-modal'
import { PostAuthorInfo } from './author-info'
import PostCommentsInfo from './comments-info'
import { PostAnnounceLayout } from './components/post-announce-layout'

interface PostAnnounceProps<T extends PostInterface | SearchPostInterface> {
  post: T
  deletePost: () => Promise<void>
  deletePostAndBanUser: (removeRecentActivitiesChecked: boolean) => Promise<void>
  handleLikeClick?: () => void
  isPinnedPost?: boolean
  comments?: CommentsSearchInterface[]
  commentsInfo?: PostCommentsInfoInterface[]
  commentsInfoError?: Error | null
}

function PostAnnounce<T extends PostInterface | SearchPostInterface>({
  post,
  deletePost,
  handleLikeClick,
  deletePostAndBanUser,
  isPinnedPost,
  comments,
  commentsInfo,
  commentsInfoError,
}: PostAnnounceProps<T>): ReactNode {
  const router = useRouter()
  const { user } = useUser()
  const { communityPath, topicPath: topicPathParam } = router.query
  const communityPathWithSlug = `/community/${communityPath}`
  const topicPath = `/community/${communityPath}/${post.topic.path}`
  const postDetailsPath = `${topicPath}/${post.path}`
  const [opened, setOpened] = useState(false)
  const hidePostContent = shouldHideUnderModerationContent(post.underModeration, user)
  const [commentsHighlightContext, setCommentsHighlightContext] = useState<{
    targetCommentId?: number
    searchHighlightedComments?: CommentsSearchInterface[]
  }>()

  const isSearchPost = isSearchPostGuard(post)

  const handleModalOpen = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement
    const isLink = target.tagName.toLowerCase() === 'a'

    if (!isLink) {
      if (globalThis) {
        globalThis.history.replaceState(null, '', postDetailsPath)
      }
      setOpened(true)
    }
  }

  const handleModalClose = (): void => {
    if (globalThis) {
      globalThis.history.replaceState(null, '', topicPathParam ? topicPath : communityPathWithSlug)
    }
    setOpened(false)
    setCommentsHighlightContext(undefined)
  }

  const handleCommentClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation()
    const { commentId } = (e.currentTarget as HTMLElement).dataset
    const numericalCommentId = Number.parseInt(commentId ?? '0', 10)
    if (numericalCommentId && !Number.isNaN(numericalCommentId)) {
      setCommentsHighlightContext({
        targetCommentId: numericalCommentId,
        searchHighlightedComments: comments,
      })
    }
    handleModalOpen(e)
  }

  const loadedCommentsInfo = commentsInfo?.find(item => item.postId === post.id)
  const postCommentsInfo: PostCommentInfoResult | undefined =
    (loadedCommentsInfo && { ok: true, value: loadedCommentsInfo }) ||
    (commentsInfoError && { ok: false, error: commentsInfoError }) ||
    (commentsInfo && { ok: false }) ||
    undefined

  if (post.status !== PostStatusEnum.Pending && !post.publishedAt) {
    Sentry.captureMessage('Regular post has no publishedAt', {
      level: 'error',
      extra: { post },
    })
    return null
  }

  if (!communityPath) return null

  const searchCommentsContext: CommentsContextType = {
    commentsType: CommentsTypeEnum.Community,
    commentsList: [],
    setComments: () => {},
    repliesList: {},
    updateReplies: () => {},
    pinnedComments: [],
    setPinnedComments: () => {},
    readOnly: true,
    extra: { community: { communityPath, postId: post.id } },
    currentSortOption: CommentSortOptions.NEWEST_FIRST,
  }

  return (
    <PostAnnounceLayout
      postData={post}
      isPinnedPost={isPinnedPost}
      isNeedTruncate={true}
      onClick={handleModalOpen}
      headerTitle={
        <PostAuthorInfo
          post={post}
          highlightedAuthorHTML={isSearchPost ? post.highlight?.author : undefined}
        />
      }
      headerActions={
        <>
          <PostPinnedActions post={post} />
          <PostActionsMenu
            post={post}
            deletePost={deletePost}
            deletePostAndBanUser={deletePostAndBanUser}
          />
        </>
      }
      footerContent={
        <div className="flex flex-col gap-2">
          {post.status === PostStatusEnum.Approved && (
            <div className="mt-[7px] flex items-center gap-[2px]">
              {handleLikeClick && (
                <div
                  onClick={e => {
                    e.stopPropagation()
                  }}
                >
                  <LikeButton
                    isLiked={post.isLiked}
                    likesCount={post.likesCount}
                    handleLikeClick={handleLikeClick}
                    postId={post.id}
                    readOnly={hidePostContent}
                  />
                </div>
              )}
              <PostCommentsInfo info={postCommentsInfo} />
            </div>
          )}
          <CommentsContext.Provider value={searchCommentsContext}>
            <CommentHighlightProvider>
              {comments?.map(comment => (
                <div
                  key={comment.id}
                  data-comment-id={comment.id}
                  role="button"
                  onClick={handleCommentClick}
                >
                  <Comment data={comment} level={0} replies={comment.children} />
                </div>
              ))}
            </CommentHighlightProvider>
          </CommentsContext.Provider>
        </div>
      }
      extraContent={
        <>
          {opened && (
            <PostModal
              deletePost={deletePost}
              deletePostAndBanUser={deletePostAndBanUser}
              opened={opened}
              onClose={handleModalClose}
              post={post}
              handleLikeClick={handleLikeClick}
              commentsHighlightContext={commentsHighlightContext}
            />
          )}
        </>
      }
    />
  )
}

export default PostAnnounce
